import { Booker } from "./types";
import React, { FC, useCallback, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBan,
    faCashRegister,
    faCheck,
    faCreditCard,
    faExclamationTriangle,
    faSlash
} from "@fortawesome/free-solid-svg-icons";
import {
    autoUpdate,
    FloatingPortal,
    offset,
    safePolygon,
    useDismiss,
    useFloating,
    useHover,
    useInteractions,
    useRole
} from "@floating-ui/react";
import { PaymentStatusComponent } from "../MollieConnect";
import { cx } from "class-variance-authority";

interface Props {
    booker: Booker;
}

const BookerStatusBadge: FC<React.PropsWithChildren<React.PropsWithChildren<Props>>> = ({ booker }) => {
    const status = booker?.status;
    const [isOpen, setIsOpen] = useState(false);
    // install floating und click interactions
    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        middleware: [offset(12)],
        strategy: "fixed",
        whileElementsMounted: autoUpdate,

        placement: "left"
    });

    const hover = useHover(context, {
        handleClose: safePolygon()
    });
    const dismiss = useDismiss(context);
    const role = useRole(context);

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        dismiss,
        role
    ]);

    const renderBookerStatus = useCallback(() => {
        switch (status) {
            case "active":
                return <FontAwesomeIcon icon={faCheck} />;

            case "inactive":
                return <FontAwesomeIcon icon={faSlash} />;

            case "cancelled":
                return <FontAwesomeIcon icon={faBan} />;

            case "waiting_for_payment":
                return <FontAwesomeIcon icon={faCreditCard} />;

            case "rejected":
                return <FontAwesomeIcon icon={faExclamationTriangle} />;
        }

    }, [status]);


    const renderPaymentStatusClassName = () => {
        switch (booker.mollie_payment_status) {
            case "paid":
                return "tw-text-success";

            case "canceled":
            case "failed":
                return "tw-text-red-500";

            default:
                return "";
        }
    };


    return <div ref={refs.setReference} {...getReferenceProps()}>
        <span className="tw-flex tw-items-center tw-gap-3">
            {renderBookerStatus()}
            {!!booker.mollie_payment_id &&
                <FontAwesomeIcon icon={faCashRegister}
                    className={cx(renderPaymentStatusClassName())}
                />}
        </span>
        {!!booker.mollie_payment_id && isOpen &&
            <FloatingPortal>
                <div ref={refs.setFloating}
                    style={floatingStyles}
                    className="tw-px-4 tw-min-h-[60px] tw-min-w-[120px] tw-py-2 tw-bg-white tw-border-solid tw-border tw-border-accents-0 tw-shadow-lg tw-rounded-xl tw-z-[999999]" {...getFloatingProps()}>
                    <PaymentStatusComponent paymentId={booker.mollie_payment_id} />
                </div>
            </FloatingPortal>}
    </div>;
};


export default BookerStatusBadge;